import { DateValue } from '@internationalized/date';
import { InventoryAggregateType } from '@rematter/frontend-lib-api-client';
import { atom, useAtom } from 'jotai';

import { inventoryOutboxEventCountsQueryAtom } from '../_graphql/queryInventoryOutboxEventCounts';
import { inventoryOutboxEventsQueryAtom } from '../_graphql/queryInventoryOutboxEvents';

import {
  dateToZonedDateTimeEnd,
  dateToZonedDateTimeStart,
} from '~/shared/utils/dateTime';

export type InventoryAuditTab = 'events' | 'inbox';

export const $inventoryAuditTab = atom<InventoryAuditTab>('events');

export const $inventoryAggregateTypeFilter = atom<
  InventoryAggregateType | undefined
>(undefined);

export const $inventoryAggregateIdFilter = atom<string | undefined>(undefined);

export const $inventoryPagination = atom<{ offset: number; limit: number }>({
  offset: 0,
  limit: 50,
});

export const $selectedInventoryEventId = atom<string | undefined>(undefined);

export const $inventoryDateRangeFilter = atom<{
  start: DateValue;
  end: DateValue;
} | null>(null);

export const $remoteInventoryOutboxEvents = inventoryOutboxEventsQueryAtom(
  (get) => {
    const aggregateType = get($inventoryAggregateTypeFilter);
    const aggregateId = get($inventoryAggregateIdFilter);
    const pagination = get($inventoryPagination);
    const dateRange = get($inventoryDateRangeFilter);

    return {
      variables: {
        aggregateTypes: aggregateType ? [aggregateType] : undefined,
        aggregateId,
        pagination,
        createdAtRange:
          dateRange?.start && dateRange?.end
            ? {
                start: dateToZonedDateTimeStart(dateRange.start),
                end: dateToZonedDateTimeEnd(dateRange.end),
              }
            : undefined,
      },
    };
  },
);

export const $remoteInventoryOutboxEventCounts =
  inventoryOutboxEventCountsQueryAtom();

export const useInventoryAuditTab = () => {
  const [tab, setTab] = useAtom($inventoryAuditTab);
  return [tab, setTab] as const;
};

export const useInventoryAggregateTypeFilter = () => {
  const [filter, setFilter] = useAtom($inventoryAggregateTypeFilter);
  const [, setAggregateId] = useAtom($inventoryAggregateIdFilter);

  const setFilterAndResetId = (value: InventoryAggregateType | undefined) => {
    setFilter(value);
    setAggregateId(undefined);
  };

  return [filter, setFilterAndResetId] as const;
};

export const useInventoryAggregateIdFilter = () => {
  const [filter, setFilter] = useAtom($inventoryAggregateIdFilter);
  return [filter, setFilter] as const;
};

export const useInventoryPagination = () => {
  const [pagination, setPagination] = useAtom($inventoryPagination);
  return [pagination, setPagination] as const;
};

export const useSelectedInventoryEventId = () => {
  const [selectedId, setSelectedId] = useAtom($selectedInventoryEventId);
  return [selectedId, setSelectedId] as const;
};

export const useInventoryDateRangeFilter = () => {
  const [filter, setFilter] = useAtom($inventoryDateRangeFilter);
  return [filter, setFilter] as const;
};
